/**
 * @description 登录回调处理插件
 * @description 处理成都登录成功后的回调，检测 refresh_token 参数并获取 access_token
 */

import { BaseMessage } from '~/utils/baseMessage'
import { getRuntimeAuthKeys } from '~/composables/useStorageKey'
import { getAuthCookieOptions } from '~/utils/storageKey'

export default defineNuxtPlugin({
  name: 'auth-callback',
  // 移除 enforce: 'pre'，确保在 Pinia 初始化之后执行
  setup(app) {
    // 只在客户端执行
    if (!import.meta.client) return

    // 等待应用挂载后再执行，确保 Pinia 已经初始化
    app.hook('app:mounted', () => {
      // 使用 nextTick 确保路由已经初始化
      nextTick(() => {
        const route = useRoute()
        const router = useRouter()
        const userStore = useUserStore()
        const runtimeConfig = useRuntimeConfig()
        const appBaseURL = runtimeConfig.app.baseURL || '/'
        userStore.restoreImsTokenFromCookies()
        if (userStore.isLoggedIn) {
          userStore.syncLoginCookie(true)
        }
        // 同步 access_token 到 Cookie，供 SSR 刷新时读取（SSR 无法访问 localStorage）
        // @reason 解决商品详情页刷新时 SSR 首屏接口返回 A00005 导致“加载失败”的问题
        const keys = getRuntimeAuthKeys()
        const tokenCookie = useCookie(keys.accessTokenCookie, getAuthCookieOptions())
        if (userStore.accessToken && tokenCookie.value !== userStore.accessToken) {
          tokenCookie.value = userStore.accessToken
        }

        function hasImsUserInfo() {
          const userInfo = userStore.userInfo || {}
          return Boolean(userInfo.cloud_code || userInfo.account_name)
        }

        /**
         * @description 同步用户信息到 store（当前仅 IMS；商城侧待 TDC 接口接入后恢复）
         * @returns {Promise<void>}
         */
        // 检测 URL 中的 refresh_token 参数
        async function syncUserInfo() {
          let hasUserInfo = false
          let firstError = null

          // TODO(TDC商城接口): 恢复商城用户信息同步
          // try {
          //   await userStore.fetchUserInfo()
          //   hasUserInfo = true
          // } catch (error) {
          //   firstError = firstError || error
          //   console.error('[Auth Callback] Failed to fetch mall user info:', error)
          // }

          try {
            await userStore.fetchImsUserInfo()
            hasUserInfo = true
          } catch (error) {
            firstError = firstError || error
            console.error('[Auth Callback] Failed to fetch IMS user info:', error)
          }

          if (!hasUserInfo && firstError) {
            throw firstError
          }
        }

        const refreshToken = route.query.refresh_token

        // refresh_token / access_token 等属于敏感信息，禁止输出到控制台或持久化日志

        if (refreshToken && typeof refreshToken === 'string') {
          // 先临时保存 refresh_token，用于调用接口
          // 注意：这里先设置 refreshToken，refreshAccessToken 方法需要使用它
          userStore.refreshToken = refreshToken
          userStore.syncRefreshTokenCookie(refreshToken)

          // 调用接口获取 access_token
          // 注意：refreshAccessToken 内部会更新 accessToken 和 tokenExpiresAt，并启动定时刷新
          // 需要获取返回结果，检查是否包含 cloud_code
          let refreshTokenResult = null
          userStore
            .refreshAccessToken()
            .then(async (result) => {
              // 保存返回结果，可能包含 cloud_code
              refreshTokenResult = result

              // refreshAccessToken 已经更新了 accessToken 和 tokenExpiresAt，并启动了定时器
              // 这里只需要确保 refreshToken 和 refreshTokenExpiresAt 也正确保存
              // 注意：直接赋值会触发 Pinia 的响应式更新，持久化插件会自动保存
              const threeDaysInMs = 3 * 24 * 60 * 60 * 1000 // 3天
              userStore.refreshToken = refreshToken
              userStore.syncRefreshTokenCookie(refreshToken)
              userStore.refreshTokenExpiresAt = Date.now() + threeDaysInMs

              // 如果 refreshAccessToken 返回了 cloud_code，先保存到 userInfo
              if (result?.cloud_code || result?.data?.cloud_code) {
                const cloudCode = result.cloud_code || result.data?.cloud_code
                if (!userStore.userInfo) {
                  userStore.userInfo = {}
                }
                userStore.userInfo.cloud_code = cloudCode
              }

              // 强制触发一次响应式更新，确保持久化插件保存
              // 通过重新赋值来触发（虽然值相同，但可以确保响应式系统检测到变化）
              userStore.accessToken = userStore.accessToken

              // token 获取成功后，延迟一下再请求用户信息，确保 token 已经保存到 store
              // 使用 nextTick 确保响应式更新已完成
              await nextTick()
              // 再延迟一小段时间，确保 token 已经生效
              await new Promise((resolve) => setTimeout(resolve, 100))

              try {
                await syncUserInfo()
              } catch (error) {
                console.error('[Auth Callback] 获取用户信息失败:', error)
                // 用户信息获取失败不影响登录流程，继续跳转
              }

              // 显示登录成功提示
              BaseMessage.success('登录成功')

              // 获取成功，移除 URL 中的 refresh_token 参数
              const query = { ...route.query }
              delete query.refresh_token

              // 跳转到原始页面
              // 优先从缓存获取（处理非首页登录后的回调），如果没有则取 URL 参数或当前路径
              const redirectBackCookie = useCookie(keys.redirectBackCookie, getAuthCookieOptions())
              let redirectUrl = query.redirect_url || route.path

              if (redirectBackCookie.value) {
                redirectUrl = redirectBackCookie.value
                redirectBackCookie.value = null // 清除缓存
              }

              delete query.redirect_url

              // 构建新的 URL（保留其他查询参数）
              // 注意：如果 redirectUrl 已经包含了查询参数，这里需要处理合并
              let finalUrl = redirectUrl
              const newQueryString = new URLSearchParams(query).toString()

              if (newQueryString) {
                finalUrl += (finalUrl.includes('?') ? '&' : '?') + newQueryString
              }

              // 如果是当前域名下的绝对路径，转为相对路径
              if (finalUrl.startsWith(window.location.origin)) {
                finalUrl = finalUrl.replace(window.location.origin, '')
              }

              // 处理 Nuxt 的 baseURL 重复问题
              // 如果路径已经包含了 appBaseURL，且我们要使用 navigateTo（它会自动补全 baseURL）
              // 则需要把路径开头的 appBaseURL 剔除，转为 Nuxt 内部的相对路径
              if (appBaseURL !== '/' && finalUrl.startsWith(appBaseURL)) {
                // 剔除 baseURL 部分，保留剩下的路径（确保以 / 开头）
                finalUrl = finalUrl.substring(appBaseURL.length - 1)
                // 注意：appBaseURL 通常以 / 结尾，如 /tenda/，这里保留最后一个 /
              }

              // 再次确保以 / 开头
              if (!finalUrl.startsWith('/')) {
                finalUrl = '/' + finalUrl
              }

              // 使用 navigateTo 处理跳转
              // 此时 finalUrl 已经是相对于 appBaseURL 的路径，navigateTo 会自动处理补全
              navigateTo(finalUrl, { replace: true })
            })
            .catch((error) => {
              console.error('[Auth Callback] 获取 access_token 失败:', error)
              // 失败时清除 token 并跳转到首页（快速清除，不需要等待退出接口）
              userStore.logout(true)
              router.replace('/')
            })
        } else {
          // 没有 refresh_token，检查是否需要恢复定时刷新
          if (userStore.refreshToken) {
            // 首先检查 refresh_token 是否过期
            // 注意：在 Pinia 中，getters 作为属性访问，不需要括号
            if (userStore.isRefreshTokenExpired) {
              // refresh_token 已过期，退出登录（快速清除，不需要等待退出接口）
              userStore.logout(true)
              return
            }

            // access_token 进入 IMS 约定的到期前 60 秒窗口后再刷新
            if (userStore.shouldRefreshAccessToken) {
              userStore
                .refreshAccessToken()
                .then(async () => {
                  // 刷新成功后，请求用户信息
                  await syncUserInfo()
                  // 尝试获取成都用户信息
                  if (userStore.userInfo?.cloud_code) {
                    try {
                      await userStore.fetchImsUserInfo()
                    } catch (error) {
                      console.error('[Auth Callback] 获取成都用户信息失败:', error)
                    }
                  }
                })
                .catch(() => {
                  // 刷新失败，logout 会清除所有信息
                })
            } else {
              // 正常，启动定时刷新
              userStore.startTokenRefreshTimer()

              // 刷新时，如果已登录，统一重新获取一次用户信息，确保深圳和成都数据同步且最新
              // @reason 解决用户刷新页面时可能导致的数据合并丢失问题（如 userType 丢失）
              if (userStore.isLoggedIn && !hasImsUserInfo()) {
                syncUserInfo().catch((error) => {
                  console.error('[Auth Callback] 获取用户信息失败:', error)
                })
              }
            }
          }
        }
      })
    })
  },
})
